export const DEFAULT_MASTER_ROLES = [
  { key: "dev", name: "Developer", desc: "ผู้พัฒนาระบบ (Dev)", color: "bg-violet-50 text-violet-700 border-violet-200", badgeColor: "bg-violet-600 text-white" },
  { key: "ceo", name: "CEO", desc: "ประธานเจ้าหน้าที่บริหาร / ผู้บริหารระดับสูง", color: "bg-rose-50 text-rose-800 border-rose-200", badgeColor: "bg-rose-600 text-white" },
  { key: "superAdmin", name: "Team Lead Admin", desc: "หัวหน้าทีมแอดมิน", color: "bg-amber-50 text-amber-800 border-amber-200", badgeColor: "bg-amber-500 text-white" },
  { key: "admin", name: "Admin", desc: "เจ้าหน้าที่แอดมิน", color: "bg-blue-50 text-blue-700 border-blue-200", badgeColor: "bg-blue-500 text-white" },
  { key: "superAccount", name: "Team Lead Accountant", desc: "หัวหน้าทีมบัญชี", color: "bg-indigo-50 text-indigo-800 border-indigo-200", badgeColor: "bg-indigo-500 text-white" },
  { key: "account", name: "Accountant", desc: "เจ้าหน้าที่บัญชี", color: "bg-cyan-50 text-cyan-700 border-cyan-200", badgeColor: "bg-cyan-500 text-white" },
  { key: "superScanner", name: "Team Lead Scanner", desc: "หัวหน้าทีมสแกนเนอร์", color: "bg-emerald-50 text-emerald-800 border-emerald-200", badgeColor: "bg-emerald-500 text-white" },
  { key: "scanner", name: "Scanner", desc: "เจ้าหน้าที่สแกนเนอร์", color: "bg-teal-50 text-teal-700 border-teal-200", badgeColor: "bg-teal-500 text-white" },
  { key: "inventory", name: "Inventory", desc: "เจ้าหน้าที่คลังสินค้า/สลาก", color: "bg-orange-50 text-orange-800 border-orange-200", badgeColor: "bg-orange-500 text-white" },
  { key: "marketing", name: "Marketing", desc: "ฝ่ายการตลาด", color: "bg-purple-50 text-purple-700 border-purple-200", badgeColor: "bg-purple-500 text-white" },
];

export const ROLE_PRIORITY_ORDER = [
  "dev",
  "ceo",
  "superadmin",
  "admin",
  "superaccount",
  "account",
  "superscanner",
  "scanner",
  "inventory",
  "marketing",
];

export const getRolePriority = (key = "") => {
  const normalized = String(key || "").toLowerCase();
  const idx = ROLE_PRIORITY_ORDER.indexOf(normalized);
  return idx >= 0 ? idx : 100;
};

/**
 * รวมบทบาททั้งหมด (ค่าเริ่มต้น + บทบาทที่สร้างเพิ่มจากหลังบ้าน) จัดเรียงตามความสำคัญ
 */
export const getEffectiveRoles = (configs = {}) => {
  const customRoles = Array.isArray(configs?.customRoles) ? configs.customRoles : [];
  // รวม roles และตัดตัวซ้ำตาม key
  const combined = [...DEFAULT_MASTER_ROLES];
  customRoles.forEach((cr) => {
    if (!cr || !cr.key) return;
    const existingIndex = combined.findIndex(
      (r) => r.key.toLowerCase() === cr.key.toLowerCase()
    );
    if (existingIndex >= 0) {
      combined[existingIndex] = { ...combined[existingIndex], ...cr };
    } else {
      combined.push({
        key: cr.key,
        name: cr.name || cr.key,
        desc: cr.desc || "บทบาทกำหนดเอง",
        color: cr.color || "bg-slate-50 text-slate-700 border-slate-200",
        badgeColor: cr.badgeColor || "bg-slate-600 text-white",
      });
    }
  });

  // จัดเรียงตามความสำคัญ (Role Priority Hierarchy)
  combined.sort((a, b) => {
    const pA = getRolePriority(a.key);
    const pB = getRolePriority(b.key);
    if (pA !== pB) return pA - pB;
    return (a.name || a.key).localeCompare(b.name || b.key);
  });

  return combined;
};

/**
 * ดึงข้อมูลของ Role Key
 */
export const getRoleInfo = (roleKey, configs = {}) => {
  const allRoles = getEffectiveRoles(configs);
  const found = allRoles.find((r) => r.key === roleKey);
  return (
    found || {
      key: roleKey,
      name: roleKey || "Staff",
      desc: "เจ้าหน้าที่ทั่วไป",
      color: "bg-slate-50 text-slate-700 border-slate-200",
      badgeColor: "bg-slate-500 text-white",
    }
  );
};

/**
 * ตรวจสอบว่าแอดมินมีสิทธิ์เข้าถึงหน้านี้หรือไม่
 */
export const hasPagePermission = (page, profileState, configs = {}) => {
  if (!page) return false;
  if (page.path === "/login") return true;

  // ตรวจสอบว่าเข้าสู่ระบบแล้วหรือยัง (หากยังไม่ล็อกอิน ไม่อนุญาตให้เข้าหน้าใดๆ ยกเว้น login)
  const isAuthUser = Boolean(
    profileState &&
      (profileState.userId ||
        profileState.role ||
        profileState.admin ||
        profileState._id ||
        profileState.username)
  );
  if (!isAuthUser) {
    return false;
  }

  if (page.path === "/logs" && profileState?.role !== "dev") return false;

  if (page.hideFromPermission) return true;

  const currentRole = profileState?.role;
  const currentUid = String(profileState?.userId || profileState?._id || "");

  // ตรวจสอบ allowedUserIds ถ้ามีกำหนดเฉพาะบุคคล
  if (page.allowedUserIds && Array.isArray(page.allowedUserIds)) {
    if (!page.allowedUserIds.includes(currentUid)) {
      return false;
    }
  }

  // ผู้พัฒนาระบบ (dev) ให้สิทธิ์ทุกหน้า (ถ้าผ่าน allowedUserIds มาแล้ว)
  if (currentRole === "dev") {
    return true;
  }

  // ตรวจสอบสิทธิ์แบบ Dynamic จาก configs.rolePermissions ถ้ามีการตั้งค่าไว้
  const dynamicPermissions = configs?.rolePermissions;
  if (
    dynamicPermissions &&
    typeof dynamicPermissions === "object" &&
    Array.isArray(dynamicPermissions[currentRole])
  ) {
    const allowedPaths = dynamicPermissions[currentRole];
    return allowedPaths.includes(page.path);
  }

  // CEO ให้สิทธิ์เข้าถึงทุกหน้าโดยค่าเริ่มต้น
  if (currentRole && currentRole.toLowerCase() === "ceo") {
    return true;
  }

  // Fallback: ใช้ permissions เริ่มต้นที่เขียนไว้ในหน้า
  if (!page.permissions || page.permissions.length === 0) {
    return true;
  }
  return page.permissions.includes(currentRole);
};
